import { Component, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { PyramidalShareholderSearchBoxComponent } from '../pyramidal-shareholder-search-box/pyramidal-shareholder-search-box.component';
import { PyramidalShareholderSearchFilter } from '../../../models/pyramidalShareholderSearchFilter';
import { PyramidalShareholderService } from '../../../services/pyramidal-shareholder.service';
import { PyramidalShareholderDto } from '../../../models/api/pyramidalShareholderDto';
import { ColumnDefinition, ListItemClickEvent } from '../../../models/columnDefinition';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { Helpers } from '../../../utilities/helpers';
import { Store } from '@ngrx/store';
import { IAppState } from '../../../store/states/app.state';
import { SetSearchFilter, SetPagination, SetSort } from '../../../store/actions/pyramidal-shareholder-list.actions';
import { selectPyramidalShareholderListFullState } from '../../../store/selectors/pyramidal-shareholder-list.selectors';
import { take, filter } from 'rxjs/operators';

@Component({
  selector: 'app-pyramidal-shareholder-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    GenericListComponent,
    NgxSpinnerModule,
    PyramidalShareholderSearchBoxComponent
  ],
  templateUrl: './pyramidal-shareholder-list.component.html',
  styleUrl: './pyramidal-shareholder-list.component.scss'
})
export class PyramidalShareholderListComponent implements AfterViewInit, OnDestroy {
  @ViewChild(GenericListComponent) genericList?: GenericListComponent<PyramidalShareholderDto>;

  pyramidalShareholders: PyramidalShareholderDto[] = [];
  totalItems = 0;
  isSearchVisible = true;

  // Pagination and sorting state
  currentPage = 1;
  pageSize = 10;
  sortBy = 'name';
  ascending = true;
  currentSearchFilter: Partial<PyramidalShareholderSearchFilter> = {};

  // Merge functionality state
  rightItem: PyramidalShareholderDto | undefined = undefined;
  matchedItems: PyramidalShareholderDto[] | undefined = undefined;

  showPyramidMemberships = false;

  private allColumnDefinitions: ColumnDefinition[] = [
    { propertyName: 'name', displayName: 'Name' },
    { propertyName: 'categoryName', displayName: 'Category' },
    { propertyName: 'subCategoryName', displayName: 'Subcategory' },
    { propertyName: 'pyramidParentsDisplay', displayName: 'Pyramid Memberships', dataType: 'list', listClickable: true },
  ];

  get columnDefinitions(): ColumnDefinition[] {
    if (this.showPyramidMemberships) {
      return this.allColumnDefinitions;
    }
    return this.allColumnDefinitions.filter(c => c.propertyName !== 'pyramidParentsDisplay');
  }

  constructor(
    private router: Router,
    private toast: HotToastService,
    private spinnerService: NgxSpinnerService,
    private pyramidalShareholderService: PyramidalShareholderService,
    private store: Store<IAppState>,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit(): void {
    // Restore state from store
    this.store.select(selectPyramidalShareholderListFullState).pipe(
      filter(state => state !== undefined && state !== null),
      take(1)
    ).subscribe(state => {
      this.currentSearchFilter = state.searchFilter;
      this.currentPage = state.currentPage;
      this.pageSize = state.pageSize;
      this.sortBy = state.sortBy;
      this.ascending = state.ascending;

      // Load pyramidal shareholders with restored state
      this.loadPyramidalShareholders();
    });
  }

  ngAfterViewInit(): void {
    // Paginator synchronization is handled by GenericListComponent
  }

  private loadPyramidalShareholders(): void {
    this.spinnerService.show();

    this.pyramidalShareholderService.searchPyramidalShareholders({
      ...this.currentSearchFilter,
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending
    }).subscribe({
      next: (response) => {
        this.pyramidalShareholders = response.data.map(ps => ({
          ...ps,
          pyramidParentsDisplay: (ps.pyramidParents || [])
            .map(p => ({
              label: `${p.parentName} (${p.year})${p.percentage != null ? ` - ${p.percentage.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%` : ''}`,
              parentId: p.parentId,
              parentType: p.parentType,
              year: p.year
            }))
        }));
        this.totalItems = response.totalCount;
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading pyramidal shareholders:', error);
        this.toast.error('Error loading pyramidal shareholders');
        this.spinnerService.hide();
      }
    });
  }

  async onCreatePyramidalShareholder(): Promise<void> {
    try {
      await this.router.navigate(['/pyramidal-shareholders/create']);
    } catch (error) {
      this.toast.error('Error navigating to Pyramidal Shareholder create (route not found)');
    }
  }

  async onEditPyramidalShareholder(pyramidalShareholder: PyramidalShareholderDto): Promise<void> {
    try {
      await this.router.navigate(['/pyramidal-shareholders/edit', pyramidalShareholder.id]);
    } catch (error) {
      this.toast.error('Error navigating to Pyramidal Shareholder edit (route not found)');
    }
  }

  onOpenPyramidalShareholderInNewTab(pyramidalShareholder: PyramidalShareholderDto): void {
    const urlTree = this.router.createUrlTree(['/pyramidal-shareholders/edit', pyramidalShareholder.id]);
    const url = this.router.serializeUrl(urlTree);
    window.open(url, '_blank');
  }

  async onListItemClick(event: ListItemClickEvent<PyramidalShareholderDto>): Promise<void> {
    if (event.column !== 'pyramidParentsDisplay' || !event.listItem?.parentId) {
      return;
    }
    // Navigate to the parent shareholder whose pyramid this one belongs to.
    const route = event.listItem.parentType === 'Large Shareholder'
      ? ['/large-shareholders/edit', event.listItem.parentId]
      : ['/pyramidal-shareholders/edit', event.listItem.parentId];
    try {
      await this.router.navigate(route);
    } catch (error) {
      this.toast.error('Error navigating to the parent shareholder (route not found)');
    }
  }

  onSortChange(sort: Sort): void {
    this.sortBy = sort.active;
    this.ascending = sort.direction === 'asc';

    // Update store
    this.store.dispatch(new SetSort({
      sortBy: this.sortBy,
      ascending: this.ascending
    }));

    // Reload data
    this.loadPyramidalShareholders();
  }

  onPageChange(event: PageEvent): void {
    // Update pagination state (API uses 1-based indexing)
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;

    // Update store
    this.store.dispatch(new SetPagination({
      currentPage: this.currentPage,
      pageSize: this.pageSize
    }));

    // Reload data
    this.loadPyramidalShareholders();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.currentSearchFilter).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  onPyramidalShareholderSearchFilter(pyramidalShareholderSearchFilter: Partial<PyramidalShareholderSearchFilter>): void {
    this.currentSearchFilter = pyramidalShareholderSearchFilter;
    this.currentPage = 1; // Reset to first page on new search

    // Update store
    this.store.dispatch(new SetSearchFilter(pyramidalShareholderSearchFilter));

    // Reset paginator UI to first page
    if (this.genericList?.paginator) {
      this.genericList.paginator.pageIndex = 0;
    }

    // Reload data
    this.loadPyramidalShareholders();
  }

  onExportToCsv(): void {
    this.spinnerService.show();

    this.pyramidalShareholderService.exportPyramidalShareholdersToCsv({
      ...this.currentSearchFilter,
      sortBy: this.sortBy,
      ascending: this.ascending
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `pyramidal_shareholders_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.spinnerService.hide();
        this.toast.success('Pyramidal shareholders exported successfully');
      },
      error: (error) => {
        console.error('Error exporting pyramidal shareholders:', error);
        this.toast.error('Error exporting pyramidal shareholders');
        this.spinnerService.hide();
      }
    });
  }

  onReadyToMatch(event: {rightItem?: PyramidalShareholderDto, matchedItems?: PyramidalShareholderDto[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onDeletePyramidalShareholder(pyramidalShareholder: PyramidalShareholderDto): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(pyramidalShareholder.name)
      .subscribe(result => {
        if (result) {
          // Store original lists for rollback in case of error
          const originalPyramidalShareholders = this.pyramidalShareholders;
          const originalTotalItems = this.totalItems;

          // 1. Optimistically remove from local state immediately
          this.pyramidalShareholders = this.pyramidalShareholders.filter(ps => ps.id !== pyramidalShareholder.id);
          this.totalItems = this.totalItems - 1;

          // 2. Call backend API
          this.spinnerService.show();
          this.pyramidalShareholderService.deletePyramidalShareholder(pyramidalShareholder.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Pyramidal shareholder deleted successfully');
              // 3. On success: Reload the list to ensure consistency with backend
              this.loadPyramidalShareholders();
            },
            error: (error) => {
              console.error('Error deleting pyramidal shareholder:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original lists
              this.pyramidalShareholders = originalPyramidalShareholders;
              this.totalItems = originalTotalItems;
              this.toast.error(`<strong>Failed to delete Pyramidal Shareholder:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onPyramidalShareholderCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select pyramidal shareholders to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog
    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original lists for rollback in case of error
        const originalPyramidalShareholders = this.pyramidalShareholders;
        const originalTotalItems = this.totalItems;

        // 1. Optimistically remove merged items from local state
        this.pyramidalShareholders = this.pyramidalShareholders.filter(ps => !bulkMergeRequest.leftIds.includes(ps.id));
        this.totalItems = this.totalItems - bulkMergeRequest.leftIds.length;

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.pyramidalShareholderService.bulkMergePyramidalShareholders(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Pyramidal shareholders merged successfully');
            // 3. On success: Clear the generic list's internal matching state and reload the list
            this.genericList?.clearMatchingState();
            this.loadPyramidalShareholders();
          },
          error: (error) => {
            console.error('Error merging pyramidal shareholders:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original lists
            this.pyramidalShareholders = originalPyramidalShareholders;
            this.totalItems = originalTotalItems;
            // Restore selection state for retry
            this.rightItem = originalPyramidalShareholders.find(ps => ps.id === bulkMergeRequest.rightId);
            this.matchedItems = originalPyramidalShareholders.filter(ps => bulkMergeRequest.leftIds.includes(ps.id));
            this.toast.error(`<strong>Failed to merge Pyramidal Shareholders:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    // Component cleanup
  }
}
